import TextInput from '@shared/components/forms/inputs/input/input.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCoins, faSync } from '@fortawesome/free-solid-svg-icons';
import { Activity } from '@activities/interfaces/activity';
import { Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { ActivityGroup } from '@activities/interfaces/activity-group';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';

import moment from 'moment';
import { Enrollment } from '@activities/interfaces/enrollment';
import { ActivityGroupWaitingContact } from '@activities/interfaces/activity-group-waiting-contact';
import { BAlert } from 'bootstrap-vue';
import { Contact } from '@contacts/interfaces/contact';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EnrollmentConfirmationModal from '@activities/components/modals/enrollment-confirmation-modal/enrollment-confirmation-modal.vue';
import EnrollmentConfirmedModal from '@activities/components/modals/enrollment-confirmed-modal/enrollment-confirmed-modal.vue';
import EnrollFamilyMemberModal from '@activities/components/modals/enroll-family-member-modal/enroll-family-member-modal.vue';
import ToolsHelper from '@core/services/tools.helper';
import BpcPostDetails from '@cms/components/details/post-details/post-details.vue';
import { Post } from '@cms/interfaces/post';
import ModalLogin from '@auth/components/modal-login/modal-login.vue';
import Textarea from '@shared/components/forms/inputs/textarea/textarea.vue';
import Select from '@shared/components/forms/inputs/select/select.vue';
import MultiSelect from '@shared/components/forms/inputs/multi-select/multi-select.vue';
import { mixins } from 'vue-class-component';
import ActivitySessionsAttendees from '@activities/mixins/activity-sessions-attendees';
import {
  addMonths,
  endOfYear,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';

library.add(faSync, faCoins);

@Component({
  name: 'bpc-activity-details',
  components: {
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-enrollment-confirmation-modal': EnrollmentConfirmationModal,
    'bpc-enrollment-confirmed-modal': EnrollmentConfirmedModal,
    'bpc-enroll-family-member-modal': EnrollFamilyMemberModal,
    'b-alert': BAlert,
    'bpc-std-form': StdForm,
    'bpc-post-details': BpcPostDetails,
    'bpc-modal-login': ModalLogin,
    'bpc-input': TextInput,
    'bpc-textarea': Textarea,
    'bpc-select': Select,
    'bpc-multi-select': MultiSelect,
  },
})
export default class ActivityDetails extends mixins(ActivitySessionsAttendees) {
  declare $refs: ActivitySessionsAttendees['$refs'] & {
    enrollmentsModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    queuedContactsModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    loginModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    confirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    waitingListConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    enrollmentConfirmationModal: InstanceType<typeof EnrollmentConfirmationModal> & {
      onOpenModal(): void;
      onCloseModal(): void;
    };
    enrollmentConfirmedModal: InstanceType<typeof EnrollmentConfirmedModal> & {
      onOpenModal(): void;
      onCloseModal(): void;
    };
    enrollFamilyMemberModal: InstanceType<typeof EnrollFamilyMemberModal> & {
      onOpenModal(): void;
      onCloseModal(): void;
    };
    cancelConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    leaveWaitingListConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    stdForm: InstanceType<typeof StdForm> & {
      formModel: AnyEntity;
      resetErrorObject(): void;
      manageSaveErrors(error): void;
    };
  };
  @Prop(String) readonly title: string;
  @Prop(String) readonly subtitle: string;
  @Prop({ type: String, required: true }) readonly alias: string;
  @Prop({ type: [String, Number], required: false }) readonly postId: string | number;

  public entityId: string | number = null;
  public activity: Activity = null;
  public provisionalActivityGroup: ActivityGroup = null;
  public provisionalEnrollment: Enrollment = null;

  public loading: boolean = false;
  public saving: boolean = false;
  public entitySubscription = null;
  public saveEntitySubscription = null;

  public successText: string = 'global.generic.successful_action';
  public errorText: string = 'global.generic.error_form_action';

  public showValidationErrorsToEnroll: boolean = false;
  public validationErrorsToEnroll: string[] = [];

  public contacts: Contact[] = [];

  public contactsToEnroll: number[] = [];
  public contactsToQueue: number[] = [];
  public provisionalQueue: ActivityGroupWaitingContact = null;
  public showValidationErrorsToQueue: boolean = false;
  public validationErrorsToQueue: string[] = [];

  public confirmedEnrollments: Enrollment[] = [];

  public disableAcceptButton = true;
  public waitingListOngoing = false;
  get disableAcceptButtonForWaitingList() {
    return this.contactsToQueue.length === 0 || this.waitingListOngoing;
  }

  public enrollmentsSubscription = null;
  public enrollments: Enrollment[] = null;

  public contactLoaded = false;
  public contactRelationsLoaded = false;
  public contactSubscription = null;
  public contactsRefreshing = false;

  public contactRelationsSubscription = null;
  public showModalLogin = false;
  public disableCancelModalAcceptButton = false;

  public waitingList: ActivityGroupWaitingContact[] = [];
  public waitingListSubscription = null;

  public activityGroupFooterIsLoading = false;

  public waitingListLoading = false;

  public answers = {};

  public showToYoungMessage: boolean = false;
  public showToOldMessage: boolean = false;

  get formattedAge() {
    const { min_age, min_month_age, max_age, max_month_age } = this.activity;

    if (!min_age && !min_month_age && !max_age && !max_month_age) {
      return this.$t('global.all_ages');
    }

    const yearsString = ` ${this.$t('global.year(s)')}`;
    const monthString = ` ${this.$t('global.month(s)')}`;

    let minimumAge = '';
    if (min_age) {
      minimumAge = `${min_age}`;
    }
    if (min_month_age && !min_age) {
      if (minimumAge) minimumAge += ' ';
      minimumAge += `${min_month_age}`;
    }

    let maximumAge = '';
    if (max_age) {
      maximumAge = `${max_age} `;
    }
    if (max_month_age && !max_age) {
      if (maximumAge) maximumAge += ' ';
      maximumAge += `${max_month_age}`;
    }

    if (min_age && min_month_age) {
      minimumAge = `${(min_age + min_month_age / 12).toFixed(1).replace('.', ',')}`;
    }

    if (max_age && max_month_age) {
      maximumAge = `${(max_age + max_month_age / 12).toFixed(1).replace('.', ',')}`;
    }

    let result = '';
    if (minimumAge) result += `${this.$t('global.from')} ${minimumAge}`;
    if (maximumAge) result += ` ${this.$t('global.to')} ${maximumAge}`;

    if (max_age) {
      result += yearsString;
    } else {
      result += monthString;
    }

    return result.trim();
  }

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  get shoppingCartActivated(): boolean {
    return this.$store.getters['centerSettings/shoppingCartActivated'];
  }

  get tenantLogo(): string {
    return this.$store.getters['centerSettings/centerSettings'].logo_url?.url;
  }

  public async getContact(): Promise<BPCGenericApiResponse> {
    return new Promise((resolve, reject) => {
      this.contactLoaded = false;

      const contactId = this.$authorizationService.getContactId();
      const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
        'contact',
        'raw',
      )}/${contactId}`;
      const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(fetchEntityPath);

      if (this.contactSubscription) {
        this.contactSubscription.unsubscribe();
      }

      this.contactSubscription = observable.subscribe(
        (res: BPCGenericApiResponse) => {
          this.contactLoaded = true;
          this.contacts.push(res.data);
          resolve(res);
          // if (this.contactRelationsLoaded) {
          //   this.getEnrollments();
          // }
        },
        error => {
          console.log(error);
          reject(error);
        },
      );
    });
  }

  public async getContactRelations(): Promise<BPCGenericApiResponse> {
    return new Promise((resolve, reject) => {
      this.contactRelationsLoaded = false;
      const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
        'contact-relations',
        'raw',
      )}`;

      const queryConfig = {
        params: {
          contact_id: this.$authorizationService.getContactId(),
        },
      };
      const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
        fetchEntityPath,
        queryConfig,
        'admin',
      );

      if (this.contactRelationsSubscription) {
        this.contactRelationsSubscription.unsubscribe();
      }

      this.contactRelationsSubscription = observable.subscribe(
        (res: BPCGenericApiResponse) => {
          if (res.data) {
            this.contactRelationsLoaded = true;
            res.data.forEach(contactRelation => {
              this.contacts.push(contactRelation['relatedContact']);
            });

            // if (this.contactLoaded) {
            //   this.getEnrollments();
            // }
          }
          resolve(res);
        },
        error => {
          console.log(error);
          reject(error);
        },
      );
    });
  }

  public async getEnrollments(reload = false): Promise<BPCGenericApiResponse> {
    return new Promise((resolve, reject) => {
      this.loading = !reload;

      const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('enrollments', 'raw')}`;

      const queryConfig = {
        params: {
          contact_id: this.contacts.map(contact => {
            return contact.id;
          }),
          per_page: -1,
          activity_id: this.entityId,
          status: ['waiting', 'approved', 'queued'],
        },
      };
      const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
        fetchEntityPath,
        queryConfig,
        'admin',
      );

      if (this.enrollmentsSubscription) {
        this.enrollmentsSubscription.unsubscribe();
      }

      this.enrollments = null;
      this.enrollmentsSubscription = observable.subscribe(
        (res: BPCGenericApiResponse) => {
          this.enrollments = res.data;
          this.loading = false;
          resolve(res);
        },
        error => {
          console.log(error);
          this.loading = false;
          reject(error);
        },
      );
    });
  }

  public async getActivityGroupWaitingContacts(reload = false): Promise<BPCGenericApiResponse> {
    return new Promise((resolve, reject) => {
      this.loading = !reload;
      this.waitingListLoading = true;

      const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
        'activity-group-waiting-contacts',
        'raw',
      )}`;

      const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
        fetchEntityPath,
        null,
        'public',
      );

      if (this.waitingListSubscription) {
        this.waitingListSubscription.unsubscribe();
      }

      this.waitingListSubscription = observable.subscribe(
        (res: BPCGenericApiResponse) => {
          this.waitingList = res.data;
          this.loading = false;
          this.waitingListLoading = false;
          resolve(res);
        },
        error => {
          this.loading = false;
          this.waitingListLoading = false;
          reject(error);
        },
      );
    });
  }

  async created() {
    if (this.alias) {
      this.getEntity();
    }
    if (this.userIsLoggedIn) {
      await this.loadUserRelatedContent();
    }
  }

  async loadUserRelatedContent() {
    // We first need the contacts and contactRelations before the enrollments
    const contactPromises = [this.getContact(), this.getContactRelations()];
    // So we wait until we have it
    await Promise.all(contactPromises);
    await this.getEnrollments(true);
    await this.getActivityGroupWaitingContacts(true);

    this.contacts.forEach(contact => {
      const emptyAnswers = {
        answer_1: '',
        answer_2: '',
        answer_3: '',
        answer_4: '',
        answer_5: '',
      };
      this.$set(this.answers, contact.id, emptyAnswers);
    });
  }

  public mounted(): void {
    this.$root.$on('updateDisableAcceptButton', $event => {
      if ($event === true) {
        this.disableAcceptButton = false;
      } else {
        this.disableAcceptButton = true;
      }
    });
  }
  public destroyed(): void {
    this.$root.$off('updateDisableAcceptButton');
  }

  public getEntity(reload = false): void {
    this.loading = !reload;
    const select = {
      columns: [
        'id',
        'code',
        'name',
        'activity_type',
        'contact_id',
        'start_date',
        'end_date',
        'published',
        'publish_start_date_time',
        'publish_end_date_time',
        'status',
        'short_description',
        'description',
        'min_age',
        'max_age',
        'min_month_age',
        'max_month_age',
        'allow_multiple_enrollment',
      ],
      relationFields: [
        'contact.name',
        'activityGroups',
        'activityGroups.*.enrollments',
        'activityGroups.*.discountCategories',
        'activityGroups.*.enable_check_on_age',
        'posts',
        'disciplines.*.name',
      ],
      computedFields: [
        'groups_amount',
        'enrollments_amount',
        'activities',
        'activityGroups.*.remaining_enrollments',
        'activityGroups.*.image_url',
      ],
      withRelations: ['activityGroups.*.enrollments', 'activityGroups'],
    };
    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'bpc-activity',
      'raw',
    )}/details/${this.alias}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
      fetchEntityPath,
      {
        params: select,
      },
      'public',
    );

    if (this.entitySubscription) {
      this.entitySubscription.unsubscribe();
    }

    this.entitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.activity = res.data;
        this.redirectIfPostNotFound();
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  public saveEntity(data: AnyEntity): void {
    this.saving = true;

    const saveEntityPath = `${this.$bpcApiRouteHelper.getApiPath(
      'shopping-cart/add-bulk-product',
      this.lang,
    )}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.post(
      saveEntityPath,
      data,
      null,
      'admin',
    );

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.onSuccessEnrollmentCreation(res.data);
        this.saving = false;
        this.disableAcceptButton = false;
        this.getEntity(true);
        this.getEnrollments(true);
        this.getActivityGroupWaitingContacts(true);
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.$refs.stdForm.resetErrorObject();
        this.$refs.stdForm.manageSaveErrors(error);
        this.manageValidationErrorsToEnroll(error);
        this.saving = false;
        this.disableAcceptButton = false;
        this.getEntity(true);
      },
    );
  }

  public saveWaitingList(data: AnyEntity): void {
    this.saving = true;

    const saveEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'activity-group-waiting-contact',
      this.lang,
    )}/bulk`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.post(
      saveEntityPath,
      data,
      null,
      'admin',
    );

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.saving = false;
        this.getActivityGroupWaitingContacts(true);
        this.$refs.waitingListConfirmationModal.onCloseModal();
        this.waitingListOngoing = false;
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.saving = false;
        console.log('error', error);
        this.waitingListOngoing = false;
      },
    );
  }
  public leaveWaitingList() {
    this.disableCancelModalAcceptButton = true;
    const data = this.provisionalQueue;
    this.removeFromWaitingList(data);
  }
  public removeFromWaitingList(data: ActivityGroupWaitingContact): void {
    this.saving = true;

    const deleteEntityPath = `${this.$bpcApiRouteHelper.getEntityPath(
      'activity-group-waiting-contact',
      this.lang,
    )}/${data.id}`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.delete(
      deleteEntityPath,
      null,
      'admin',
    );

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.saving = false;
        this.getActivityGroupWaitingContacts(true);
        this.$refs.leaveWaitingListConfirmationModal.onCloseModal();
        this.disableCancelModalAcceptButton = false;
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.saving = false;
        this.disableCancelModalAcceptButton = false;
        console.log('error', error);
      },
    );
  }

  manageValidationErrorsToEnroll(error) {
    if (error.response.status === 422) {
      if (error.response.data.errors) {
        const errors = error.response.data.errors;
        this.validationErrorsToEnroll = [];
        Object.keys(error.response.data.errors).forEach(field => {
          this.validationErrorsToEnroll.push(errors[field][0]);
        });
        this.showValidationErrorsToEnroll = true;
      }
    }
  }

  public updateEntity(data: Enrollment): void {
    this.saving = true;

    const updateEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('enrollment')}/${
      this.provisionalEnrollment.id
    }`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.put(
      updateEntityPath,
      data,
      null,
      'admin',
    );

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.onSuccessEnrollmentCancelation();
        this.saving = false;
      },
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.saving = false;
      },
    );
  }
  public cancelEntity(data: Enrollment): void {
    this.saving = true;

    const updateEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('enrollment')}/cancel/${
      data.id
    }`;
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.put(
      updateEntityPath,
      {
        id: data.id,
      },
      null,
      'admin',
    );

    if (this.saveEntitySubscription) {
      this.saveEntitySubscription.unsubscribe();
    }
    this.saveEntitySubscription = observable.subscribe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (res: BPCGenericApiResponse) => {
        this.$bpcMessage.showSuccessMessage({ text: this.successText });
        this.onSuccessEnrollmentCancelation();
        this.saving = false;
        this.disableCancelModalAcceptButton = false;
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      error => {
        this.$bpcMessage.showErrorMessage({ text: this.errorText });
        this.saving = false;
        this.disableCancelModalAcceptButton = false;
      },
    );
  }

  public showEnrollements(activityGroup: ActivityGroup): void {
    console.log('showEnrollements');
    this.provisionalActivityGroup = activityGroup;
    if (activityGroup) {
      this.$refs.enrollmentsModal.onOpenModal(activityGroup.id);
    }
    // this.contactsToEnroll = [];
  }
  public onCreateEnrollment(activityGroup: ActivityGroup): void {
    this.provisionalActivityGroup = activityGroup;
    this.validationErrorsToEnroll = [];
    this.showValidationErrorsToEnroll = false;
    if (!this.userIsLoggedIn) {
      this.showLoginModal();
    } else {
      if (activityGroup) {
        this.$refs.confirmationModal.onOpenModal(activityGroup.id);
      }
      this.contactsToEnroll = [];
    }
  }

  public onAddToWaitingList(activityGroup: ActivityGroup): void {
    this.provisionalActivityGroup = activityGroup;
    this.validationErrorsToQueue = [];
    this.showValidationErrorsToQueue = false;
    if (!this.userIsLoggedIn) {
      this.showLoginModal();
    } else {
      if (activityGroup) {
        this.$refs.waitingListConfirmationModal.onOpenModal(activityGroup.id);
      }
      this.contactsToQueue = [];
    }
  }
  public showQueuedContacts(activityGroup: ActivityGroup): void {
    this.provisionalActivityGroup = activityGroup;
    if (activityGroup) {
      this.$refs.queuedContactsModal.onOpenModal(activityGroup.id);
    }
    this.contactsToQueue = [];
  }

  showLoginModal() {
    this.showModalLogin = true;
  }

  async onSuccessLogin() {
    await this.loadUserRelatedContent();
    this.onCreateEnrollment(this.provisionalActivityGroup);
  }

  public onCloseModalLogin() {
    this.showModalLogin = false;
  }
  public createEnrollment() {
    this.disableAcceptButton = true;

    const validAnswers = this.areAnswersValid();

    if (validAnswers) {
      // const data = {
      //   activity_id: this.entityId,
      //   activity: this.entityId,
      //   activity_group_id: this.provisionalActivityGroup.id,
      //   activity_group: this.provisionalActivityGroup.id,
      //   contact_ids:
      //     this.contacts.length > 1
      //       ? this.contactsToEnroll
      //       : [this.$authorizationService.getContactId()],
      //   creator_contact_id: this.$authorizationService.getContactId(),
      //   status: this.initialStatusForEnrollment(),
      //   form_config_name: 'my-contact-with-contact-relations',
      //   date: moment().format('YYYY-MM-DD'),
      //   terms_and_conditions_accepted: this.$refs.stdForm.formModel.terms_and_conditions_accepted,
      //   online_enrollment: true,
      //   discounts: this.provisionalActivityGroup.discounts.map(function(discount) {
      //     return discount['id'];
      //   }),
      //   answers: this.answers,
      // };
      const data = {
        productType: 'enrollment',
        productCategoryId: this.provisionalActivityGroup.id,
        contactIds:
          this.contacts.length > 1
            ? this.contactsToEnroll
            : [this.$authorizationService.getContactId()],
        additionalParameters: {
          terms_and_conditions_accepted: this.$refs.stdForm.formModel.terms_and_conditions_accepted,
          answers: this.answers,
          creator_contact_id: this.$authorizationService.getContactId(),
        },
      };
      this.saveEntity(data);
    } else {
      this.$bpcMessage.showErrorMessage({
        text: this.$t('global.modals.enrollments.mandatory.answers.error') as string,
      });
      this.saving = false;
      this.disableAcceptButton = false;
    }
  }

  public areAnswersValid() {
    let valid = true;
    const contact_ids =
      this.contacts.length > 1
        ? this.contactsToEnroll
        : [this.$authorizationService.getContactId()];

    contact_ids.forEach(contactId => {
      for (let index = 1; index <= 5; index++) {
        if (this.activity[`question_${index}_mandatory`]) {
          if (this.answers[contactId][`answer_${index}`] === '') {
            valid = false;
            const input = this.$refs[`concact_${contactId}_answer_${index}`];
          }
        }
      }
    });
    return valid;
  }

  public createWaitingList() {
    this.waitingListOngoing = true;
    const data = {
      activity_id: this.entityId,
      activity_group_id: this.provisionalActivityGroup.id,
      activity_group: this.provisionalActivityGroup.id,
      contact_id: this.$authorizationService.getContactId(),
      contact_ids:
        this.contacts.length > 1
          ? this.contactsToQueue
          : [this.$authorizationService.getContactId()],
    };
    this.saveWaitingList(data);
  }
  public onSuccessEnrollmentCreation(enrollments: Enrollment[]) {
    this.confirmedEnrollments = enrollments;
    this.$refs.confirmationModal.onCloseModal();
    if (this.shoppingCartActivated) {
      this.$refs.enrollmentConfirmationModal.onOpenModal();
    } else {
      this.$refs.enrollmentConfirmedModal.onOpenModal();
    }
    this.$root.$emit('refresh-orders');
  }

  public onCancelEnrollment(activityGroup: ActivityGroup, contact_id: number = null): void {
    const enrollment = this.enrollments.find((enrollment: Enrollment) => {
      return (
        enrollment.activity_group_id == activityGroup.id &&
        (!contact_id || contact_id === enrollment.contact_id)
      );
    });
    this.provisionalEnrollment = enrollment;
    this.$refs.cancelConfirmationModal.onOpenModal(enrollment.id);
  }
  public cancelEnrollment() {
    this.disableCancelModalAcceptButton = true;
    const data = this.provisionalEnrollment;
    data.status = 'cancelled';
    this.cancelEntity(data);
  }
  public onSuccessEnrollmentCancelation() {
    this.$refs.cancelConfirmationModal.onCloseModal();
    this.getEntity();
    this.getEnrollments();
    this.$root.$emit('refresh-orders');
  }

  public contactsHaveEnrollment(contacts) {
    return contacts.find(contact => this.hasEnrollmentInThisActivity(contact.id));
  }

  public getNbContactsEnrolled(activity_group_id) {
    let nbContactsEnrolled = 0;
    this.contacts.forEach(contact => {
      if (this.hasEnrollmentInThisActivityGroup(activity_group_id, contact.id)) {
        nbContactsEnrolled++;
      }
    });
    return nbContactsEnrolled;
  }
  public getNbContactsQueued(activity_group_id) {
    let nbContactsQueued = 0;
    this.contacts.forEach(contact => {
      if (this.isQueuedInThisActivityGroup(activity_group_id, contact.id)) {
        nbContactsQueued++;
      }
    });
    return nbContactsQueued;
  }
  public onLeaveWaitingList(activityGroup: ActivityGroup, contact_id: number = null): void {
    const list = this.waitingList.find((list: ActivityGroupWaitingContact) => {
      return (
        list.activity_group_id == activityGroup.id &&
        (!contact_id || contact_id === list.contact_id)
      );
    });
    this.provisionalQueue = list;
    this.$refs.leaveWaitingListConfirmationModal.onOpenModal(list.id);
  }
  public hasEnrollmentInThisActivityGroup(
    activity_group_id: number,
    contact_id: number = null,
  ): boolean {
    if (this.enrollments) {
      const enrollment = this.enrollments.find((enrollment: Enrollment) => {
        return (
          enrollment.activity_group_id == activity_group_id &&
          (!contact_id || contact_id === enrollment.contact_id)
        );
      });
      if (enrollment) {
        return true;
      }
    }
    return false;
  }
  public hasEnrollmentInThisActivity(contact_id: number = null): boolean {
    if (this.enrollments) {
      const enrollment = this.enrollments.find((enrollment: Enrollment) => {
        return (
          enrollment.status == 'approved' &&
          enrollment.activity_id == this.activity.id &&
          (!contact_id || contact_id === enrollment.contact_id)
        );
      });
      if (enrollment) {
        return true;
      }
    }
    return false;
  }
  public isQueuedInThisActivityGroup(
    activity_group_id: number,
    contact_id: number = null,
  ): boolean {
    if (this.waitingList) {
      const contactIsQueued = this.waitingList.find((list: ActivityGroupWaitingContact) => {
        return (
          list.activity_group_id == activity_group_id &&
          (!contact_id || contact_id === list.contact_id)
        );
      });
      if (contactIsQueued) {
        return true;
      }
    }
    return false;
  }

  public activityGroupInPublishRange(publishStart: string, publishEnd: string): boolean {
    const today = moment();

    if (publishStart == null && publishEnd == null) {
      return true;
    } else {
      const sameOrAfterStart = today.isSameOrAfter(publishStart);
      const sameOrBeforeEnd = today.isSameOrBefore(publishEnd);

      if (publishStart != null && publishEnd == null) {
        return sameOrAfterStart;
      } else if (publishStart == null && publishEnd != null) {
        return sameOrBeforeEnd;
      } else {
        return sameOrAfterStart && sameOrBeforeEnd;
      }
    }
  }

  public checkEnrollmentStartDateAndEndDate(enrollmentStartTime, enrollmentEndTime): boolean {
    return (
      this.checkEnrollmentStartDate(enrollmentStartTime) &&
      this.checkEnrollmentEndDate(enrollmentEndTime)
    );
  }

  public checkEnrollmentStartDate(enrollmentStartTime): boolean {
    const today = moment();
    let result = true;
    if (enrollmentStartTime) {
      const enrollmentStart = moment(enrollmentStartTime);
      if (today.isSameOrBefore(enrollmentStart)) {
        result = false;
      }
    }
    return result;
  }

  public checkEnrollmentEndDate(enrollmentEndTime): boolean {
    const today = moment();
    let result = true;
    if (enrollmentEndTime) {
      const enrollmentEnd = moment(enrollmentEndTime);
      if (today.isSameOrAfter(enrollmentEnd)) {
        result = false;
      }
    }
    return result;
  }
  public formatDate(date): string {
    if (date) {
      return moment(String(date)).format('DD/MM/YYYY');
    }
  }
  public formatTime(date): string {
    if (date) {
      return moment(String(date)).format('HH:mm');
    }
  }

  public addContactToEnroll($event) {
    if ($event.target.checked) {
      if (!this.contactsToEnroll.includes($event.target.value)) {
        const contact_id = $event.target.value;
        const emptyAnswers = {
          answer_1: '',
          answer_2: '',
          answer_3: '',
          answer_4: '',
          answer_5: '',
        };
        this.$set(this.answers, contact_id, emptyAnswers);
        this.contactsToEnroll.push($event.target.value);
      }
    } else {
      if (this.contactsToEnroll.includes($event.target.value)) {
        const index = this.contactsToEnroll.indexOf($event.target.value);
        if (index > -1) {
          this.contactsToEnroll.splice(index, 1);
        }
      }
      if (this.answers[$event.target.value]) {
        delete this.answers[$event.target.value];
      }
    }
  }

  public addContactToWaitingList($event) {
    if ($event.target.checked) {
      if (!this.contactsToQueue.includes($event.target.value)) {
        this.contactsToQueue.push($event.target.value);
      }
    } else {
      if (this.contactsToQueue.includes($event.target.value)) {
        const index = this.contactsToQueue.indexOf($event.target.value);
        if (index > -1) {
          this.contactsToQueue.splice(index, 1);
        }
      }
    }
  }
  public showActivityGroup(activity_group) {
    return (
      activity_group.published &&
      this.activityGroupInPublishRange(
        activity_group.publish_start_date_time,
        activity_group.publish_end_date_time,
      )
    );
  }

  public showRemaining(activity_group, activity) {
    return (
      (!this.hasEnrollmentInThisActivityGroup(activity_group.id) || this.contacts.length > 1) && // If the contact has contact related we show the remaining though it has enrollment in the group while the nexts conditions are true
      (!this.hasEnrollmentInThisActivity() ||
        activity.allow_multiple_enrollment ||
        this.contacts.length > 1) && // If the contact has contact related we show the remaining though it has enrollment in the activity while the nexts conditions are true
      this.checkEnrollmentStartDateAndEndDate(
        activity_group.enrollment_start_date_time,
        activity_group.enrollment_end_date_time,
      ) &&
      activity_group.remaining_enrollments > 0 &&
      activity_group.enrollment_allowed
    );
  }

  get userIsLoggedIn(): boolean {
    return this.$authService.loggedIn;
  }
  get allowedLoginAfter(): boolean {
    return true; // TODO implement the logic here
  }

  public checkIfUserCanInitTheEnrollmentProcess(activity_group, activity) {
    if (!this.userIsLoggedIn && this.allowedLoginAfter) {
      return true;
    }
    return (
      this.enrollments &&
      (!this.hasEnrollmentInThisActivityGroup(activity_group.id) || this.contacts.length > 1) &&
      (!this.hasEnrollmentInThisActivity() ||
        activity.allow_multiple_enrollment ||
        this.contacts.length > 1)
    );
  }
  public showEnrollButton(activity_group, activity) {
    const startDateTime = activity_group.enrollment_start_date_time;
    const endDateTime = activity_group.enrollment_end_date_time;

    const enrollmentAllowed =
      this.checkEnrollmentStartDateAndEndDate(startDateTime, endDateTime) &&
      activity_group.remaining_enrollments !== 0 && // If the group have not max enrollments, the remaining enrollments it's -1
      activity_group.enrollment_allowed;

    const userCanInitTheEnrollmentProcess = this.checkIfUserCanInitTheEnrollmentProcess(
      activity_group,
      activity,
    );

    return enrollmentAllowed && userCanInitTheEnrollmentProcess;
  }

  public showAddInWaitingListButton(activity_group, activity) {
    const startDateTime = activity_group.enrollment_start_date_time;
    const endDateTime = activity_group.enrollment_end_date_time;
    const enrollmentAllowed =
      this.checkEnrollmentStartDateAndEndDate(startDateTime, endDateTime) &&
      activity_group.with_waiting_list &&
      activity_group.remaining_enrollments === 0 && // If the group have not max enrollments, the remaining enrollments it's -1
      activity_group.enrollment_allowed;

    const userCanInitTheEnrollmentProcess = this.checkIfUserCanInitTheEnrollmentProcess(
      activity_group,
      activity,
    );

    if (!this.userIsLoggedIn) {
      return enrollmentAllowed;
    } else {
      const contactId = this.$authorizationService.getContactId();
      const isAlreadyEnrolled =
        this.hasEnrollmentInThisActivityGroup(activity_group.id, contactId) ||
        this.hasEnrollmentInThisActivity(contactId);

      return enrollmentAllowed && userCanInitTheEnrollmentProcess && !isAlreadyEnrolled;
    }
  }

  public showNotAvailable(activity_group) {
    return (
      activity_group.max_enrollments &&
      !activity_group.remaining_enrollments &&
      this.checkEnrollmentEndDate(activity_group.enrollment_end_date_time) &&
      this.checkEnrollmentStartDate(activity_group.enrollment_start_date_time)
    );
  }
  public showEnrollmentEnd(activity_group) {
    return (
      !this.checkEnrollmentEndDate(activity_group.enrollment_end_date_time) ||
      !activity_group.enrollment_allowed
    );
  }
  public showCheckboxForEnrollmentInModal(contact) {
    contact = this.addTooYoungTooOldParams(contact);
    return (
      this.provisionalActivityGroup &&
      !contact.too_old &&
      !contact.too_young &&
      !contact.has_not_birthdate &&
      !this.hasEnrollmentInThisActivityGroup(this.provisionalActivityGroup.id, contact.id) &&
      (!this.hasEnrollmentInThisActivity(contact.id) || this.activity.allow_multiple_enrollment) &&
      this.provisionalActivityGroup.remaining_enrollments !== 0 &&
      (this.provisionalActivityGroup.remaining_enrollments == -1 ||
        this.contactsToEnroll.length < this.provisionalActivityGroup.remaining_enrollments ||
        this.contactsToEnroll.includes(contact.id.toString()))
    );
  }

  /**
   * Adds flags to indicate if a contact is too young, too old, or has no birthdate.
   * @param contact - The contact object to update with flags.
   * @returns The updated contact object.
   */
  public addTooYoungTooOldParams(contact) {
    // Initialize flags for age conditions
    contact.too_young = false;
    contact.too_old = false;
    contact.has_not_birthdate = false;
    // Perform age verification
    const result = this.canEnrollByAge(contact);
    // Update contact flags based on age verification result
    if (result === 'too_young') {
      contact.too_young = true;
    } else if (result === 'too_old') {
      contact.too_old = true;
    } else if (result === 'has_not_birthdate') {
      contact.has_not_birthdate = true;
    }

    return contact;
  }

  /**
   * Checks if a contact has a birthdate.
   * @param contact - The contact object to check.
   * @returns True if the contact has a birthdate, otherwise false.
   */
  public contactHasBirthdate(contact): boolean {
    return contact?.contact_individual?.birthdate;
  }

  /**
   * Determines if a contact is eligible for enrollment based on age.
   * @param contact - The contact object to verify.
   * @returns The result of age verification: 'ok', 'too_young', 'too_old', or 'has_not_birthdate'.
   */
  public canEnrollByAge(contact) {
    // Flags to show message in the contact list.
    const result = 'ok'; // ok, too_young, too_old, has_not_birthdate

    // Check if age verification is enabled for this activity group
    if (this.provisionalActivityGroup?.enable_check_on_age) {
      if (!this.contactHasBirthdate(contact)) {
        return 'has_not_birthdate';
      }

      // Get the birth date of the contact
      const birthDate = parseISO(contact.contact_individual.birthdate);

      // Determine type of age verification (by age or birth year)
      const typeOfCheck = this.provisionalActivityGroup.age_of_participant;

      // Get the tolerance in months for age check
      const toleranceMonths = this.provisionalActivityGroup.tolerance_in_months_for_age_check || 0;

      // Check if verification is age based
      if (typeOfCheck === 'age_based') {
        return this.verifyAgeBasedEnrollment(contact, birthDate, toleranceMonths);
      }
      // Check if verification is birth year based
      else if (typeOfCheck === 'birth_based') {
        return this.verifyBirthYearBasedEnrollment(contact, birthDate, toleranceMonths);
      }
    }
    return result;
  }

  /**
   * Verifies age-based eligibility for enrollment.
   * @param contact - The contact object to verify.
   * @param birthDate - The birth date of the contact.
   * @param toleranceMonths - Tolerance in months for age check.
   * @returns The result of age verification: 'ok', 'too_young', or 'too_old'.
   */
  public verifyAgeBasedEnrollment(contact, birthDate, toleranceMonths): string {
    // Get the start date of the activity group or the activity
    const startDate = this.provisionalActivityGroup.start_date
      ? parseISO(this.provisionalActivityGroup.start_date)
      : this.activity.start_date
      ? parseISO(this.activity.start_date)
      : null;

    if (!startDate) {
      // If no dates, no checks needed
      return 'ok';
    }
    // Get the minimum and maximum allowed age
    const minAge = this.provisionalActivityGroup.min_age;
    const minMonth = this.provisionalActivityGroup.min_month_age;
    const maxAge = this.provisionalActivityGroup.max_age;
    const maxMonth = this.provisionalActivityGroup.max_month_age;

    if (!minAge && !minMonth && !maxAge && !maxMonth) {
      // If no limits, no checks needed
      return 'ok';
    }

    // Calculate minimum and maximum birth dates based on age limits
    let minDate = null;
    let maxDate = null;

    // Only calculate minDate if there is a maxAge or a maxMonth (older age limit)
    if (maxAge !== null && maxAge !== undefined) {
      // Adjust for months if they are specified
      if (maxMonth !== null && maxMonth !== undefined) {
        minDate = subYears(startDate, maxAge);
        minDate = subMonths(minDate, maxMonth);
      } else {
        minDate = subYears(startDate, maxAge + 1);
      }
    }

    // We only calculate MaxDate if there is a mine or MinMonth (lower age limit)
    if (minAge !== null && minAge !== undefined) {
      maxDate = subYears(startDate, minAge);
      // Adjust for months if they are specified
      if (minMonth !== null && minMonth !== undefined) {
        maxDate = subMonths(maxDate, minMonth);
      }
    }

    return this.checkAgeLimits(contact, birthDate, minDate, maxDate, toleranceMonths);
  }

  /**
   * Verifies birth year-based eligibility for enrollment.
   * @param contact - The contact object to verify.
   * @param birthDate - The birth date of the contact.
   * @param toleranceMonths - Tolerance in months for age check.
   * @returns The result of age verification: 'ok', 'too_young', or 'too_old'.
   */
  public verifyBirthYearBasedEnrollment(contact, birthDate, toleranceMonths): string {
    const minBirthYear = this.provisionalActivityGroup.min_birth_year
      ? parseInt(this.provisionalActivityGroup.min_birth_year)
      : null;
    const maxBirthYear = this.provisionalActivityGroup.max_birth_year
      ? parseInt(this.provisionalActivityGroup.max_birth_year)
      : null;

    if (!minBirthYear && !maxBirthYear) {
      // No limits, no checks
      return 'ok';
    }

    // Calculate the minimum and maximum birth dates applying tolerance if specified
    let minDate = minBirthYear ? startOfYear(new Date(minBirthYear, 0, 1)) : null;
    let maxDate = maxBirthYear ? endOfYear(new Date(maxBirthYear, 11, 31)) : null;

    if (minDate && toleranceMonths) {
      minDate = subMonths(minDate, toleranceMonths);
    }

    if (maxDate && toleranceMonths) {
      maxDate = addMonths(maxDate, toleranceMonths);
    }

    if (minDate && isBefore(birthDate, minDate)) {
      return 'too_old';
    }
    if (maxDate && isAfter(birthDate, maxDate)) {
      return 'too_young';
    }

    return 'ok';
  }

  /**
   * Checks if birth date falls within specified limits.
   * @param contact - The contact object to verify.
   * @param birthDate - The birth date of the contact.
   * @param minDate - The minimum allowed birth date.
   * @param maxDate - The maximum allowed birth date.
   * @param toleranceMonths - Tolerance in months for age check.
   * @returns The result of age verification: 'ok', 'too_young', or 'too_old'.
   */
  public checkAgeLimits(contact, birthDate, minDate, maxDate, toleranceMonths = 0): string {
    // Apply tolerance in months to minimum and maximum dates if specified
    if (toleranceMonths) {
      minDate = minDate ? subMonths(minDate, toleranceMonths) : null;
      maxDate = maxDate ? addMonths(maxDate, toleranceMonths) : null;
    }

    // Only verify "too_old" if there is a minDate (older age limit)
    if (minDate && (isBefore(birthDate, minDate) || isEqual(birthDate, minDate))) {
      return 'too_old';
    }

    // Only verify "too_young" if there is a maxDate (lower age limit)
    if (maxDate && isAfter(birthDate, maxDate)) {
      return 'too_young';
    }

    return 'ok';
  }

  public showCheckboxForWaitingListInModal(contact) {
    return (
      this.provisionalActivityGroup &&
      !this.hasEnrollmentInThisActivityGroup(this.provisionalActivityGroup.id, contact.id) &&
      !this.isQueuedInThisActivityGroup(this.provisionalActivityGroup.id, contact.id)
    );
  }
  public showNotAvailableInModal(contact) {
    return (
      this.provisionalActivityGroup &&
      !this.hasEnrollmentInThisActivityGroup(this.provisionalActivityGroup.id, contact.id) &&
      this.provisionalActivityGroup.remaining_enrollments > 0 &&
      this.contactsToEnroll.length >= this.provisionalActivityGroup.remaining_enrollments &&
      !this.contactsToEnroll.includes(contact.id.toString())
    );
  }
  public showAlreadyEnrolledGroupInModal(contact) {
    return (
      this.provisionalActivityGroup &&
      this.hasEnrollmentInThisActivityGroup(this.provisionalActivityGroup.id, contact.id)
    );
  }
  public showAlreadyEnrolledActivityInModal(contact) {
    return (
      this.provisionalActivityGroup &&
      this.hasEnrollmentInThisActivity(contact.id) &&
      !this.activity.allow_multiple_enrollment &&
      !this.showAlreadyEnrolledGroupInModal(contact)
    );
  }

  public showAlreadyQueuedGroupInModal(contact) {
    return (
      this.provisionalActivityGroup &&
      this.isQueuedInThisActivityGroup(this.provisionalActivityGroup.id, contact.id)
    );
  }
  public formatPrice(value: string) {
    return ToolsHelper.formatPrice(value);
  }

  private initialStatusForEnrollment() {
    if (this.provisionalActivityGroup.article_id) {
      return 'waiting'; // We create the enrollment in waiting status. This status block the place and have a timer. If the timer expired then the status is changed to waiting_not_blocked
    } else {
      return 'approved'; // We create the enrollment in approved status.
    }
  }

  public redirectIfPostNotFound() {
    if (this.postId != this.currentPostId) {
      this.$router.push({
        name: 'profile-activity-details',
        params: {
          activityId: String(this.entityId),
        },
      });
    }
  }

  public checkIfActivityPublished(activity) {
    return (
      activity.published &&
      this.checkActivityStartDate(activity.publish_start_date_time) &&
      this.checkActivityEndDate(activity.publish_end_date_time)
    );
  }

  public checkActivityStartDate(activityStartTime): boolean {
    const today = moment();
    let result = true;
    if (activityStartTime) {
      const activityStart = moment(activityStartTime);
      if (today.isSameOrBefore(activityStart)) {
        result = false;
      }
    }
    return result;
  }

  public checkActivityEndDate(activityEndTime): boolean {
    const today = moment();
    let result = true;
    if (activityEndTime) {
      const activityEnd = moment(activityEndTime);
      if (today.isSameOrAfter(activityEnd)) {
        result = false;
      }
    }
    return result;
  }

  public onEnrollFamilyMember() {
    this.$refs.enrollFamilyMemberModal.onOpenModal();
  }

  async refreshContacts() {
    this.contactsRefreshing = true;
    this.contacts = [];
    this.contactsToEnroll = [];
    await this.loadUserRelatedContent();
    this.contactsRefreshing = false;
  }

  get previousPost(): Post {
    const posts = this.activity.posts;
    if (posts) {
      const postIndex = posts.findIndex(post => post.id == this.postId);
      if (postIndex > 0) {
        return posts[postIndex - 1] || null;
      }
    }
    return null;
  }
  get nextPost(): Post {
    const posts = this.activity.posts;
    if (posts) {
      const postIndex = posts.findIndex(post => post.id == this.postId);
      const length = posts.length - 1;
      if (postIndex < length && length > 0) {
        return posts[postIndex + 1] || null;
      }
    }
    return null;
  }
  get currentPostId(): number {
    return this.activity?.posts
      ? this.activity.posts.find(post => post.id == this.postId)?.id || null
      : null;
  }

  get canManageEnrollments(): boolean {
    return this.$authorizationService.hasPermissions([
      `manage_enrollments`,
      `manage_own_enrollments`,
    ]);
  }

  get profileReadOnly(): boolean {
    return this.$authorizationService.hasRole('contact_read');
  }

  enrollmentsModalConfig(): ConfirmationModalConfig {
    return {
      id: 'enrollments-modal',
      title: this.$t('global.modals.enrollments.title'),
      body: this.$t('global.modals.enrollments.body', {
        activity_group_name: this.provisionalActivityGroup
          ? this.provisionalActivityGroup.name
          : null,
      }) as string,
      cancelText: this.$t('global.buttons.close') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }
  queuedContactsModalConfig(): ConfirmationModalConfig {
    return {
      id: 'queued-contacts-modal',
      title: this.$t('global.modals.queued_contacts.title'),
      body: this.$t('global.modals.queued_contacts.body', {
        activity_group_name: this.provisionalActivityGroup
          ? this.provisionalActivityGroup.name
          : null,
      }) as string,
      cancelText: this.$t('global.buttons.close') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }
  confirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'enroll-modal',
      title: this.$t('global.modals.enroll.title'),
      body: this.$t('global.modals.enroll.body', {
        activity_group_name: this.provisionalActivityGroup
          ? this.provisionalActivityGroup.name
          : null,
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }
  waitingListConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'waiting-list-modal',
      title: this.$t('global.modals.waiting_list.title'),
      body: this.$t('global.modals.waiting_list.body', {
        activity_group_name: this.provisionalActivityGroup
          ? this.provisionalActivityGroup.name
          : null,
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }
  leaveWaitingListConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'leave-waiting-list-modal',
      title: this.$t('global.modals.leave_waiting_list.title'),
      body: this.$t('global.modals.leave_waiting_list.body', {
        activity_group_name: this.provisionalQueue
          ? this.provisionalQueue.activity_group.name
          : null,
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }
  cancelConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'cancel-enrollment-modal',
      title: this.$t('global.modals.cancel-enrollment.title'),
      body: this.$t('global.modals.cancel-enrollment.body', {
        activity_group_name: this.provisionalEnrollment
          ? this.provisionalEnrollment.activity_group.name
          : null,
      }) as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.confirm') as string,
    };
  }

  formatOptions(options) {
    const optionsSplitted = options.split(';');
    const optionsArray = [];
    optionsSplitted.forEach(option => {
      const trimmedOption = option.trim();
      if (trimmedOption !== '') {
        optionsArray.push({ text: trimmedOption, value: trimmedOption });
      }
    });

    return optionsArray;
  }

  get startDate(): string {
    if (this.activity?.start_date) {
      return moment(this.activity?.start_date, 'YYYY-MM-DD')
        .locale('fr')
        .format('ddd DD/MM/YYYY');
    }
    return null;
  }

  get endDate(): string {
    if (this.activity?.end_date) {
      return moment(this.activity?.end_date, 'YYYY-MM-DD')
        .locale('fr')
        .format('ddd DD/MM/YYYY');
    }
    return null;
  }

  get hasActivityGroups(): boolean {
    let hasGroups = false;
    if (this.activity.activity_groups && this.activity.activity_groups.length) {
      this.activity.activity_groups.forEach(group => {
        console.log(group);
        if (group.published) {
          console.log('has activity groups');
          hasGroups = true;
        }
      });
    }
    return hasGroups;
  }

  showActivityGroupDetails: boolean = false;
  selectedActivityGroup: Partial<ActivityGroup> = {};

  openActivityGroupDetails(activityGroup: Partial<ActivityGroup>): void {
    this.selectedActivityGroup = activityGroup;
    this.showActivityGroupDetails = true;
  }

  closeActivityGroupDetails(): void {
    this.showActivityGroupDetails = false;
    this.selectedActivityGroup = {};
  }

  @Watch('postId', { immediate: true, deep: true })
  onPostIdChange() {
    this.redirectIfPostNotFound();
  }
  @Watch('alias')
  onChangeAlias() {
    this.getEntity();
  }
}
